import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { User } from '../../shared/models';
import { MobileAuthService } from './mobile-auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private auth0 = inject(Auth0Service);
  private mobileAuth = inject(MobileAuthService);
  private http = inject(HttpClient);
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  // Track subscriptions for cleanup
  private subscriptions: Subscription[] = [];

  // Callback when user changes (for conversation history)
  private userChangeCallbacks: Array<(userId: string) => void> = [];

  // Store user info from expired ID token
  private expiredTokenUserInfo: {
    email?: string;
    sub?: string;
    identities?: any[];
  } | null = null;

  // Decode JWT payload (without verification)
  private decodeJwtPayload(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Error decoding JWT payload:', error);
      return null;
    }
  }

  constructor() {
    console.log('🚀 AuthService constructor called');
    console.log('🔍 Platform is native:', Capacitor.isNativePlatform());

    // Clear expired tokens on startup
    console.log('🧹 Clearing expired tokens on startup');
    this.mobileAuth.clearExpiredTokens();

    // Check if user is already logged in (from localStorage)
    const savedUser = localStorage.getItem('currentUser');
    console.log('🔍 Saved user exists in localStorage:', !!savedUser);

    if (savedUser) {
      console.log('🔍 Saved user data:', JSON.parse(savedUser));
      // Only load the user if we have a valid token (synchronous check)
      this.checkTokenAndLoadUserSync(JSON.parse(savedUser));
    } else {
      console.log('🔍 No saved user found, setting currentUser to null');
      this.currentUserSubject.next(null);
    }

    // Listen to Auth0 authentication state
    this.subscriptions.push(
      this.auth0.isAuthenticated$.subscribe((isAuthenticated) => {
        console.log('🔐 Auth0 authentication state:', isAuthenticated);
        if (isAuthenticated) {
          this.subscriptions.push(
            this.auth0.user$.subscribe((auth0User) => {
              console.log('🔐 Auth0.user$ emitted:', auth0User);
              console.log('🔐 Auth0 user.sub value:', auth0User?.sub);
              console.log('🔐 Auth0 user.sub type:', typeof auth0User?.sub);
              console.log('🔐 Auth0 user.sub: [valid]');

              if (auth0User) {
                console.log('🔐 Auth0 user logged in:', auth0User);
                console.log('🔐 Auth0 user.sub (user ID):', auth0User.sub);
                console.log('🔐 Auth0 user.email:', auth0User.email);
                console.log(
                  '🔐 Auth0 user identities:',
                  auth0User['identities']
                );

                // Use user info from expired token if available (for email and identities)
                const userEmail =
                  auth0User.email || this.expiredTokenUserInfo?.email || '';
                const userIdentities =
                  auth0User['identities'] ||
                  this.expiredTokenUserInfo?.identities ||
                  [];

                console.log(
                  '🔐 Using email from:',
                  auth0User.email ? 'Auth0 user' : 'expired token'
                );
                console.log(
                  '🔐 Using identities from:',
                  auth0User['identities'] ? 'Auth0 user' : 'expired token'
                );
                console.log('🔐 User email available:', !!userEmail);
                console.log('🔐 User email value:', userEmail);

                // Check if this is a linked account
                const identities = userIdentities;
                const primaryIdentity =
                  identities.find((identity: any) => identity.isPrimary) ||
                  identities[0];
                console.log(
                  '🔐 Primary identity provider:',
                  primaryIdentity?.provider
                );
                console.log(
                  '🔐 Primary identity user_id:',
                  primaryIdentity?.user_id
                );
                console.log(
                  '🔐 All identities:',
                  identities.map((id: any) => ({
                    provider: id.provider,
                    user_id: id.user_id,
                    isPrimary: id.isPrimary,
                  }))
                );

                // UNIFIED USER IDENTITY STRATEGY:
                // 1. If email exists → Use email as primary identifier (for linked accounts)
                // 2. If no email → Use Auth0 user ID as identifier (for Facebook users, etc.)
                let userId: string;
                let storageIdentifier: string;

                if (userEmail && userEmail.trim()) {
                  // Email exists - use it as the primary identifier for unified storage
                  userId = userEmail.trim();
                  storageIdentifier = 'email';
                  console.log('🔐 Email-based user identity:', userId);
                } else {
                  // No email - use Auth0 user ID as identifier
                  userId =
                    (auth0User.sub && auth0User.sub.trim()) || 'anonymous';
                  storageIdentifier = 'auth0_id';
                  console.log('🔐 Auth0 ID-based user identity:', userId);
                }

                console.log('🔐 Storage identifier type:', storageIdentifier);

                // Note: We're using the unified identity strategy above
                // Provider-specific IDs are no longer needed since we prioritize email-based storage

                console.log(
                  '🔐 Final userId before creating user object:',
                  userId
                );
                console.log('🔐 Final userId type:', typeof userId);
                console.log('🔐 Final userId: [valid]');

                const user: User = {
                  email: userEmail,
                  name: auth0User.name || userEmail || 'User',
                  userId: userId,
                };
                this.currentUserSubject.next(user);
                localStorage.setItem('currentUser', JSON.stringify(user));
                console.log('✅ User set in AuthService:', user);

                // Fetch user role and permissions from Auth0 token (await to ensure they're loaded)
                this.fetchUserRole().then(() => {
                  console.log('✅ User roles and permissions loaded');
                });

                // Notify conversation history service with user ID
                console.log(
                  '🔐 Notifying conversation history service with user ID:',
                  userId
                );
                this.notifyUserChange(userId);
              }
            })
          );
        }
      })
    );

    // Handle Auth0 errors
    this.subscriptions.push(
      this.auth0.error$.subscribe((error) => {
        if (error) {
          // Check if error is empty object (common during initialization in non-HTTPS environments)
          const isEmptyError =
            Object.keys(error).length === 0 ||
            (!error.message &&
              !(error as any).error &&
              !(error as any).error_description);

          if (isEmptyError && Capacitor.isNativePlatform()) {
            // Empty error in mobile WebView during local dev is usually harmless
            // Auth0 may fail to initialize fully on HTTP, but mobile auth flow works via deep links
            console.log(
              'ℹ️ Auth0 initialization warning (empty error) - this is normal in local dev WebView'
            );
            return; // Skip logging empty errors in mobile
          }

          // Log all Auth0 errors for debugging Facebook login issues
          console.error('❌ Auth0 error:', error);
          console.error('❌ Auth0 error details:', {
            message: error.message,
            error: (error as any).error,
            errorDescription: (error as any).error_description,
            code: (error as any).code,
            details: (error as any).details,
          });

          // Handle specific Facebook login errors
          if (
            (error as any).error === 'access_denied' ||
            (error as any).error === 'consent_required'
          ) {
            console.error('❌ Facebook login was denied or requires consent');
            console.error(
              '❌ This might be due to Facebook app configuration or user permissions'
            );
          } else if ((error as any).error === 'login_required') {
            console.log(
              'ℹ️ Auth0: Login required (normal when not authenticated)'
            );
          } else if (
            (error as any).error === 'missing_refresh_token' ||
            error.message?.includes('Missing Refresh Token')
          ) {
            console.log(
              'ℹ️ Auth0: Refresh token not available (normal when refresh tokens are disabled)'
            );
          } else if (error.message?.includes('Service not found')) {
            console.error(
              '❌ Auth0 service configuration issue - audience may not be properly configured'
            );
          } else {
            console.error(
              '❌ Unexpected Auth0 error - this might affect Facebook login'
            );
          }
        }
      })
    );
  }

  login(
    email: string,
    name: string,
    roles?: string[],
    permissions?: string[]
  ): void {
    console.log('🔐 Traditional login called with:', {
      email,
      name,
      roles,
      permissions,
    });

    // Check if this is a different user logging in
    const oldUser = this.currentUserSubject.value;
    const oldUserEmail = oldUser?.email;
    const isDifferentUser = oldUserEmail && oldUserEmail !== email.trim();

    if (isDifferentUser) {
      console.log('🔄 Different user detected, clearing old user data');
      console.log('🔄 Old user:', oldUserEmail);
      console.log('🔄 New user:', email.trim());

      // Clear old user data from localStorage
      localStorage.removeItem('currentUser');

      // Don't emit null first - immediately emit the new user to prevent UI flicker
      // The cleanup happens in the background
      this.completeLogin(email, name, roles, permissions);
    } else {
      this.completeLogin(email, name, roles, permissions);
    }
  }

  private completeLogin(
    email: string,
    name: string,
    roles?: string[],
    permissions?: string[]
  ): void {
    // For traditional login (email/password), use email as the identifier
    const userId = email.trim() || 'anonymous';
    const user: User = {
      email,
      name,
      userId,
      roles: roles || [],
      permissions: permissions || [],
    };

    console.log('🔐 Traditional login user created:', user);
    this.currentUserSubject.next(user);
    localStorage.setItem('currentUser', JSON.stringify(user));

    // Notify conversation history service
    this.notifyUserChange(userId);
  }

  logout(): void {
    console.log('🚪 Logout called - clearing all tokens and user data');
    console.log('🚨 AUTH SERVICE LOGOUT - Starting logout process');

    // Debug what tokens exist before clearing
    const idToken = localStorage.getItem('auth0_id_token');
    const accessToken = localStorage.getItem('auth0_access_token');
    const localToken = localStorage.getItem('local_token');
    console.log('🔍 Tokens before logout:', {
      idToken: !!idToken,
      accessToken: !!accessToken,
      localToken: !!localToken,
    });

    console.log('🚨 AUTH SERVICE LOGOUT - Setting currentUser to null');
    this.currentUserSubject.next(null);
    console.log('🚨 AUTH SERVICE LOGOUT - Clearing localStorage');
    localStorage.removeItem('currentUser');

    // Clear all Auth0 tokens
    localStorage.removeItem('auth0_access_token');
    localStorage.removeItem('auth0_id_token');
    localStorage.removeItem('local_token');

    console.log('🧹 All tokens cleared from localStorage');

    // Reset to anonymous user for conversation history
    this.notifyUserChange('anonymous');

    // Also logout from Auth0 if authenticated
    this.subscriptions.push(
      this.auth0.isAuthenticated$.subscribe((isAuth) => {
        if (isAuth) {
          console.log('🔐 Auth0 logout initiated');
          this.auth0.logout({
            logoutParams: { returnTo: window.location.origin },
          });
        }
      })
    );

    // For mobile platforms, also logout from mobile auth service
    if (Capacitor.isNativePlatform()) {
      console.log('📱 Mobile platform detected - calling mobile logout');
      this.mobileAuth.logout();
    }
  }

  clearExpiredTokens(): void {
    // Clear expired Auth0 tokens
    const mobileAuth0Token = localStorage.getItem('auth0_access_token');
    if (mobileAuth0Token) {
      try {
        const tokenPayload = JSON.parse(atob(mobileAuth0Token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        if (tokenPayload.exp && tokenPayload.exp <= currentTime) {
          console.log('🧹 Clearing expired Auth0 tokens');
          localStorage.removeItem('auth0_access_token');
          localStorage.removeItem('auth0_id_token');
        }
      } catch (error) {
        console.log('🧹 Clearing invalid Auth0 tokens');
        localStorage.removeItem('auth0_access_token');
        localStorage.removeItem('auth0_id_token');
      }
    }
  }

  private checkTokenAndLoadUserSync(user: User): void {
    console.log('🔍 checkTokenAndLoadUserSync called with user:', user);
    console.log('🔍 Platform is native:', Capacitor.isNativePlatform());

    // For mobile platforms, check if we have a valid token synchronously
    if (Capacitor.isNativePlatform()) {
      const mobileAuth0Token = localStorage.getItem('auth0_access_token');
      console.log('🔍 Mobile Auth0 token exists:', !!mobileAuth0Token);

      if (mobileAuth0Token) {
        const isExpired = this.mobileAuth.isTokenExpired(mobileAuth0Token);
        console.log('🔍 Mobile Auth0 token expired:', isExpired);

        if (!isExpired) {
          // Try to extract user email from token to verify it matches
          try {
            const tokenPayload = JSON.parse(
              atob(mobileAuth0Token.split('.')[1])
            );
            const tokenEmail = tokenPayload.email || tokenPayload.sub;
            const userEmail = user.email?.trim();

            console.log('🔍 Token email:', tokenEmail);
            console.log('🔍 User email:', userEmail);

            // If token email doesn't match user email, clear old user and don't load
            if (tokenEmail && userEmail && tokenEmail !== userEmail) {
              console.warn(
                '⚠️ Token email does not match user email, clearing old user'
              );
              console.warn('⚠️ Token belongs to:', tokenEmail);
              console.warn('⚠️ localStorage user is:', userEmail);
              localStorage.removeItem('currentUser');
              this.currentUserSubject.next(null);
              return;
            }
          } catch (e) {
            console.warn('⚠️ Could not parse token to verify user match:', e);
          }

          console.log(
            '🔐 Valid mobile token found, loading user from localStorage'
          );
          this.currentUserSubject.next(user);

          // Notify conversation history service with email
          this.notifyUserChange(user.email || 'anonymous');

          // Fetch user role and permissions from Auth0 token
          this.fetchUserRole().then(() => {
            console.log(
              '✅ User roles and permissions loaded from mobile token'
            );
          });

          return;
        }
      }
    }

    // For web platforms, check if we have a local token
    const localToken = localStorage.getItem('local_token');
    console.log('🔍 Local token exists:', !!localToken);

    if (localToken) {
      console.log('🔐 Valid local token found, loading user from localStorage');
      this.currentUserSubject.next(user);

      // Notify conversation history service with email
      this.notifyUserChange(user.email || 'anonymous');

      return;
    }

    // No valid token found, clear saved user
    console.log('🔐 No valid token found, clearing saved user');
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
  }

  private async checkTokenAndLoadUser(user: User): Promise<void> {
    try {
      // Check if we have a valid token
      const token = await this.getAuthToken();
      if (token) {
        console.log('🔐 Valid token found, loading user from localStorage');
        this.currentUserSubject.next(user);
      } else {
        console.log('🔐 No valid token found, clearing saved user');
        localStorage.removeItem('currentUser');
        this.currentUserSubject.next(null);
      }
    } catch (error) {
      console.log('🔐 Error checking token, clearing saved user:', error);
      localStorage.removeItem('currentUser');
      this.currentUserSubject.next(null);
    }
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  isLoggedIn(): boolean {
    return this.currentUserSubject.value !== null;
  }

  async getAuthToken(): Promise<string | null> {
    console.log('🔐 getAuthToken() called');
    console.log('🔍 Platform is native:', Capacitor.isNativePlatform());
    console.log('🔍 Current URL:', window.location.href);
    console.log('🔍 User agent:', navigator.userAgent);

    // Debug localStorage contents
    console.log('🔍 localStorage contents:');
    const auth0Keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('auth0')) {
        auth0Keys.push(key);
        console.log(
          `  - ${key}: ${localStorage.getItem(key)?.substring(0, 50)}...`
        );
      }
    }
    console.log('🔍 All Auth0 keys found:', auth0Keys);

    // For mobile platforms, use mobile Auth0 tokens directly
    if (Capacitor.isNativePlatform()) {
      console.log('📱 Mobile platform detected');

      // Try ID token first (standard JWT that backend can verify)
      const mobileIdToken = localStorage.getItem('auth0_id_token');
      console.log('🔍 Mobile ID token exists:', !!mobileIdToken);
      console.log('🔍 Mobile ID token: [valid]');

      if (mobileIdToken) {
        console.log(
          '🔍 Mobile ID token first 100 chars:',
          mobileIdToken.substring(0, 100)
        );
        const isExpired = this.mobileAuth.isTokenExpired(mobileIdToken);
        console.log('🔍 Mobile ID token expired:', isExpired);

        if (!isExpired) {
          console.log('✅ Using valid mobile ID token');
          return mobileIdToken;
        } else {
          console.log('❌ Mobile ID token is expired');
        }
      } else {
        console.log('⚠️ Mobile ID token is null/undefined');
        // ID token is missing but we have an access token
        const mobileAuth0Token = localStorage.getItem('auth0_access_token');
        console.log('🔍 Mobile access token exists:', !!mobileAuth0Token);

        if (mobileAuth0Token) {
          console.log(
            '🔍 Mobile access token first 100 chars:',
            mobileAuth0Token.substring(0, 100)
          );
        }

        if (
          mobileAuth0Token &&
          !this.mobileAuth.isTokenExpired(mobileAuth0Token)
        ) {
          console.log(
            '⚠️ ID token missing but access token exists - need to re-login'
          );
          console.log(
            '💡 Please log out and log back in to get a fresh ID token'
          );
          // Clear the invalid state
          this.logout();
          return null;
        }
      }

      // Fallback to access token if ID token is not available (deprecated - for backward compatibility)
      const mobileAuth0Token = localStorage.getItem('auth0_access_token');
      console.log('🔍 Mobile access token exists:', !!mobileAuth0Token);

      if (mobileAuth0Token) {
        const isExpired = this.mobileAuth.isTokenExpired(mobileAuth0Token);
        console.log('🔍 Mobile access token expired:', isExpired);

        if (!isExpired) {
          console.log('⚠️ Using mobile access token (ID token preferred)');
          return mobileAuth0Token;
        } else {
          console.log('❌ Mobile access token is expired');
        }
      }

      // Fallback to local token for development
      const localToken = localStorage.getItem('local_token');
      console.log('🔍 Local token exists:', !!localToken);
      if (localToken) {
        console.log('✅ Using local token for development');
        return localToken;
      }

      console.log('❌ No valid tokens found for mobile platform');
      return null;
    }

    // For web platform, use Auth0 SDK
    console.log('🌐 Web platform detected - using Auth0 SDK');

    try {
      // Try to get the ID token from Auth0 SDK cache (localStorage)
      console.log('🔍 Searching for ID token in Auth0 SDK cache...');
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('@@auth0spajs@@') && key.includes('@@user@@')) {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              const parsed = JSON.parse(value);
              if (parsed.id_token) {
                console.log('✅ Found ID token in Auth0 cache!');
                console.log('🔍 ID token length:', parsed.id_token.length);
                console.log(
                  '🔍 ID token first 100 chars:',
                  parsed.id_token.substring(0, 100)
                );

                // Check if token is expired
                try {
                  const isExpired = this.mobileAuth.isTokenExpired(
                    parsed.id_token
                  );
                  console.log('🔍 ID token expired:', isExpired);

                  if (!isExpired) {
                    console.log('✅ Using valid ID token from Auth0 cache');
                    return parsed.id_token;
                  } else {
                    console.log(
                      '❌ ID token is expired, but extracting user info from it'
                    );
                    // Even though the token is expired, we can still decode it to get user information
                    try {
                      const decodedToken = this.decodeJwtPayload(
                        parsed.id_token
                      );
                      if (decodedToken) {
                        console.log('🔍 Decoded expired ID token user info:', {
                          email: decodedToken.email,
                          sub: decodedToken.sub,
                          identities: decodedToken.identities,
                        });
                        // Store the user info from the expired token for later use
                        this.expiredTokenUserInfo = {
                          email: decodedToken.email,
                          sub: decodedToken.sub,
                          identities: decodedToken.identities,
                        };

                        // Check if we have email and identities, if not, try to refresh
                        const hasEmail = !!(
                          decodedToken.email && decodedToken.email.trim()
                        );
                        const hasIdentities = !!decodedToken.identities;
                        console.log('🔍 Expired token has email:', hasEmail);
                        console.log(
                          '🔍 Expired token has identities:',
                          hasIdentities
                        );

                        if (!hasEmail || !hasIdentities) {
                          console.log(
                            '🔄 Expired token missing email/identities - this is normal for Facebook users'
                          );

                          // For Facebook users without email/identities, we'll use the Auth0 sub as the primary identifier
                          // This is expected behavior for Facebook social logins in Auth0
                          console.log(
                            'ℹ️ Facebook user detected - using Auth0 sub as primary identifier'
                          );
                        }
                      }
                    } catch (decodeError) {
                      console.log(
                        '🔍 Could not decode expired ID token:',
                        decodeError
                      );
                    }
                  }
                } catch (tokenError) {
                  console.log('❌ Error validating ID token:', tokenError);
                }
              }
            } catch (parseError) {
              console.log('❌ Error parsing Auth0 cache entry:', parseError);
            }
          }
        }
      }

      // Fallback to access token if ID token not found
      console.log(
        '🔍 ID token not found, attempting to get access token silently...'
      );

      let auth0Token: string | null = null;
      try {
        // Try to get ID token first with getAccessTokenSilently
        auth0Token =
          (await this.auth0.getAccessTokenSilently().toPromise()) || null;
        console.log('🔍 Auth0 SDK returned token:', !!auth0Token);

        if (auth0Token) {
          console.log('🔍 Auth0 token length:', auth0Token.length);
          console.log(
            '🔍 Auth0 token first 100 chars:',
            auth0Token.substring(0, 100)
          );

          // If we got a token, try to get fresh ID token claims to check for email/identities
          try {
            const freshIdToken = await this.auth0.idTokenClaims$
              .pipe(take(1))
              .toPromise();
            if (freshIdToken && freshIdToken.__raw) {
              const freshHasEmail = !!(
                freshIdToken.email && freshIdToken.email.trim()
              );
              const freshHasIdentities =
                !!freshIdToken['https://kyrgz-translation-api/identities'];
              console.log('🔍 Fresh ID token has email:', freshHasEmail);
              console.log(
                '🔍 Fresh ID token has identities:',
                freshHasIdentities
              );

              // Update stored user info with fresh data
              if (freshHasEmail || freshHasIdentities) {
                this.expiredTokenUserInfo = {
                  email: freshIdToken.email,
                  sub: freshIdToken['sub'],
                  identities:
                    freshIdToken['https://kyrgz-translation-api/identities'],
                };
                console.log('✅ Updated user info with fresh ID token data');
                return freshIdToken.__raw; // Return the fresh ID token instead of access token
              }
            }
          } catch (idTokenError) {
            console.log(
              '⚠️ Could not get fresh ID token claims:',
              idTokenError
            );
          }
        }
      } catch (silentError: any) {
        console.log('🔍 Silent token refresh failed:', silentError.message);

        // If user is not authenticated, don't try to get tokens
        if (
          silentError.error === 'login_required' ||
          silentError.message?.includes('Login required')
        ) {
          console.log('🔍 User not authenticated, skipping token retrieval');
          return null;
        }

        // For other errors, log and return null
        console.log('🔍 Error getting access token silently:', silentError);
        return null;
      }

      if (auth0Token) {
        console.log('🔍 Auth0 token length:', auth0Token.length);
        console.log(
          '🔍 Auth0 token first 100 chars:',
          auth0Token.substring(0, 100)
        );

        // Check if token is expired
        try {
          const isExpired = this.mobileAuth.isTokenExpired(auth0Token);
          console.log('🔍 Web Auth0 token expired:', isExpired);

          if (isExpired) {
            console.log('❌ Web Auth0 token is expired');
            return null;
          }
          console.log(
            '⚠️ Using access token (ID token preferred but not available)'
          );
          return auth0Token;
        } catch (tokenError) {
          console.log('❌ Error validating web Auth0 token:', tokenError);
          return null;
        }
      } else {
        console.log('⚠️ Auth0 SDK returned null token');
      }
    } catch (error: any) {
      // Don't log "Login required" as an error since it's expected when user isn't authenticated
      if (
        error?.error === 'login_required' ||
        error?.message?.includes('Login required')
      ) {
        console.log('🔍 User not authenticated (expected when not logged in)');
      } else {
        console.log('❌ Error getting web Auth0 token:', error);
        console.log('❌ Error details:', {
          message: error?.message,
          error: error?.error,
          errorDescription: error?.error_description,
        });
      }
    }

    // Fallback to local token for web development
    const localToken = localStorage.getItem('local_token');
    console.log('🔍 Local token exists:', !!localToken);
    if (localToken) {
      console.log('✅ Using local token for development');
      return localToken;
    }

    console.log(
      '🔍 No valid tokens found - will use anonymous mode (normal when not logged in)'
    );
    return null;
  }

  /**
   * Extract roles and permissions from Auth0 token
   * This will update the current user with their roles and permissions
   */
  async fetchUserRole(): Promise<void> {
    const currentUser = this.currentUserSubject.value;

    if (!currentUser || !currentUser.email) {
      console.log('⚠️ No current user to fetch role for');
      return;
    }

    try {
      let claims: any = null;

      // On mobile, decode the ID token directly from localStorage
      if (Capacitor.isNativePlatform()) {
        console.log('📱 Mobile platform: decoding ID token from localStorage');
        const mobileIdToken = localStorage.getItem('auth0_id_token');

        if (mobileIdToken) {
          try {
            // Decode JWT token (it's base64 encoded)
            const tokenParts = mobileIdToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              claims = payload;
              console.log('✅ Successfully decoded mobile ID token');
            }
          } catch (decodeError) {
            console.error('❌ Error decoding mobile ID token:', decodeError);
          }
        } else {
          console.log('⚠️ No mobile ID token found in localStorage');
        }
      } else {
        // On web, use the Auth0 SDK
        console.log('🌐 Web platform: getting claims from Auth0 SDK');
        claims = await this.auth0.idTokenClaims$.pipe(take(1)).toPromise();
      }

      if (claims) {
        console.log('🔍 Auth0 token claims:', claims);

        // Extract roles and permissions from token
        const roles = claims['https://kyrgyz-translation-api/roles'] || [];
        const permissions =
          claims['https://kyrgyz-translation-api/permissions'] || [];

        console.log('👑 Auth0 roles:', roles);
        console.log('🔐 Auth0 permissions:', permissions);

        // Determine primary role for backward compatibility
        let primaryRole: 'admin' | 'customer' = 'customer';
        if (roles.includes('admin')) {
          primaryRole = 'admin';
        }

        // Update the current user with roles and permissions
        const updatedUser: User = {
          ...currentUser,
          role: primaryRole,
          roles: roles,
          permissions: permissions,
        };

        this.currentUserSubject.next(updatedUser);
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));

        console.log('✅ User roles and permissions updated from Auth0 token');
        console.log('✅ Primary role:', primaryRole);
        console.log('✅ All roles:', roles);
        console.log('✅ All permissions:', permissions);
      } else {
        console.log(
          '⚠️ No Auth0 claims found, falling back to backend role check'
        );
        await this.fetchUserRoleFromBackend();
      }
    } catch (error) {
      console.error('❌ Error fetching user role from Auth0 token:', error);
      // Fallback to backend role check
      await this.fetchUserRoleFromBackend();
    }
  }

  /**
   * Fallback method to fetch user role from backend (Firestore)
   * This is used when Auth0 token doesn't contain roles
   */
  private async fetchUserRoleFromBackend(): Promise<void> {
    const currentUser = this.currentUserSubject.value;

    if (!currentUser || !currentUser.email) {
      console.log('⚠️ No current user to fetch role for');
      return;
    }

    try {
      const token = await this.getAuthToken();
      if (!token) {
        console.log('⚠️ No auth token available to fetch role');
        return;
      }

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });

      // Fetch user data from backend which includes the role
      const response = await this.http
        .get<any>(`${environment.apiUrl}/api/word-usage/stats`, { headers })
        .toPromise();

      if (response && response.success && response.role) {
        // Update the current user with the role from backend
        const updatedUser: User = {
          ...currentUser,
          role: response.role as 'admin' | 'customer',
          roles: [response.role], // Convert single role to array
          permissions: [], // No permissions from backend fallback
        };

        this.currentUserSubject.next(updatedUser);
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));

        console.log(
          '✅ User role updated from backend fallback:',
          response.role
        );
      } else {
        console.log('⚠️ No role found in backend response, keeping default');
      }
    } catch (error) {
      console.error('❌ Error fetching user role from backend:', error);
    }
  }

  // Register a callback for when user changes (for conversation history)
  onUserChange(callback: (userEmail: string) => void): void {
    this.userChangeCallbacks.push(callback);
  }

  /**
   * Check if current user has a specific role
   */
  hasRole(role: string): boolean {
    const currentUser = this.currentUserSubject.value;
    return currentUser?.roles?.includes(role) || false;
  }

  /**
   * Check if current user has admin role
   */
  isAdmin(): boolean {
    const currentUser = this.currentUserSubject.value;
    // Check both single role and roles array for admin
    return (
      !!(currentUser && currentUser.role === 'admin') ||
      !!(
        currentUser &&
        currentUser.roles &&
        currentUser.roles.includes('admin')
      )
    );
  }

  /**
   * Check if current user has a specific permission
   */
  hasPermission(permission: string): boolean {
    const currentUser = this.currentUserSubject.value;
    const hasPermission =
      currentUser?.permissions?.includes(permission) || false;

    return hasPermission;
  }

  /**
   * Check if current user has any of the specified permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser?.permissions) return false;
    return permissions.some((permission) =>
      currentUser.permissions!.includes(permission)
    );
  }

  /**
   * Check if current user has all of the specified permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser?.permissions) return false;
    return permissions.every((permission) =>
      currentUser.permissions!.includes(permission)
    );
  }

  private notifyUserChange(userEmail: string): void {
    this.userChangeCallbacks.forEach((callback) => callback(userEmail));
  }

  /**
   * Handle Auth0 redirect callback for web platform
   * This method exchanges the authorization code for tokens
   */
  async handleRedirectCallback(): Promise<void> {
    console.log('🔗 handleRedirectCallback() called');

    try {
      // Call Auth0 SDK's handleRedirectCallback to exchange code for tokens
      await this.auth0.handleRedirectCallback();
      console.log('✅ Auth0 handleRedirectCallback completed successfully');
    } catch (error: any) {
      console.error('❌ Error in handleRedirectCallback:', error);

      // Handle specific Auth0 errors
      if (error.error === 'access_denied') {
        console.log('⚠️ User denied access during login');
      } else if (error.error === 'consent_required') {
        console.log('⚠️ User consent is required');
      } else if (error.error === 'login_required') {
        console.log('⚠️ User needs to login');
      } else if (error.message?.includes('Service not found')) {
        console.log(
          '⚠️ Auth0 service not found - check audience configuration'
        );
      } else {
        console.log('⚠️ Unknown Auth0 error:', error.message || error);
      }

      throw error;
    }
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.subscriptions.forEach((subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
    this.subscriptions = [];
  }
}
